import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import ArrowIcon from "@/design-system/icons/arrow-icon";
import type { IDropdownOptionV2, IDropdownV2 } from "@Components/Dropdown/DropdownTypes";
import * as stylex from "@stylexjs/stylex";

import { colors } from "./../../../styles/tokens.stylex";

import * as css from "./DropdownV2.module.css";

export const DropdownV2: FC<IDropdownV2> = ({ options, onChange, defaultOption, onClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultOption || options[0]);
    const setOnChange = (option: IDropdownOptionV2) => {
        setIsOpen(false);
        if (onChange && onChange(option) === false) {
            return;
        }

        setSelectedOption(option);
    };
    const optionsRef = useRef<HTMLDivElement>();
    const dropdownRef = useRef<HTMLDivElement>();
    const dropdownWidthRef = useRef(0);

    useEffect(() => {
        if (isOpen) {
            const { width, height } = optionsRef.current.getBoundingClientRect();
            dropdownWidthRef.current = dropdownRef.current.getBoundingClientRect().width;

            optionsRef.current.animate(
                [
                    {
                        right: "1px",
                        height: 0,
                        opacity: 0,
                    },
                    {
                        right: "1px",
                        height: `${height}px`,
                        opacity: 1,
                    },
                ],
                {
                    fill: "forwards",
                    easing: "ease-in-out",
                    duration: 150,
                },
            );

            dropdownRef.current.animate(
                [
                    {
                        width: `${dropdownWidthRef.current}px`,
                    },
                    {
                        width: `${width}px`,
                    },
                ],
                {
                    fill: "none",
                    easing: "ease-in-out",
                    duration: 150,
                },
            ).onfinish = () => {
                dropdownRef.current.style.width = `${width}px`;
            };
        } else if (dropdownWidthRef.current > 0) {
            dropdownRef.current.style.width = "unset";
        }
    }, [isOpen]);

    const onDropdownClick = () => {
        setIsOpen(!isOpen);

        if (onClick) {
            onClick();
        }
    };

    return (
        <div className={css.dropdownV2Component} ref={dropdownRef}>
            <button className={css.selected} type="button" onClick={onDropdownClick}>
                {selectedOption.label}
                <ArrowIcon direction={isOpen ? "up" : "down"} styleX={styles.arrow} />
            </button>
            {isOpen && (
                <div className={css.options} ref={optionsRef}>
                    {options.map((option) => (
                        <button
                            key={option.value}
                            className={css.option}
                            onClick={() => setOnChange(option)}
                            type="button"
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const styles = stylex.create({
    arrow: {
        color: colors.fg_Primary,
        strokeWidth: 3,
    },
});
